import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import Spinner from 'react-bootstrap/Spinner'
import { analyzeReceipt, type AnalysisMode, type Receipt } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCapabilities } from '../../hooks/useCapabilities'
import { dostupneRezimy, nazevRozpoznavani } from '../../hooks/useZpracovaniUctenek'

export type ReceiptRecheckMenuProps = {
    receipt: Receipt
    /** Účtenka po žádosti - čeká na nový výsledek. */
    onRequested: (receipt: Receipt) => void
    label?: string
    variant?: string
    size?: 'sm'
    disabled?: boolean
}

const HLASKA: Record<AnalysisMode, string> = {
    SERVER: 'Účtenka se rozpoznává znovu.',
    MCP: 'Účtenka čeká na AI asistenta.',
    BOTH: 'Účtenku přečte aplikace i AI asistent — výsledky pak porovnáte.',
}

/**
 * „Rozpoznáno špatně? Přečíst znovu" - aplikací, AI asistentem přes MCP,
 * nebo oběma, aby šly výsledky porovnat. Výsledek, který přijde po žádosti,
 * nahradí ten špatný; při volbě obojího platí první hotový a druhý čeká
 * k porovnání.
 */
export function ReceiptRecheckMenu({ receipt, onRequested, label, variant = 'outline-secondary', size, disabled }: ReceiptRecheckMenuProps) {
    const { data: capabilities } = useCapabilities()
    const [odesila, setOdesila] = useState(false)

    // Bez rozpoznávání na serveru nemá „aplikací" co nabídnout.
    const rezimy = dostupneRezimy(capabilities).filter((rezim) => rezim !== 'SERVER' || capabilities?.receiptAnalysis)
    if (rezimy.length === 0) {
        return null
    }

    const nastroj = nazevRozpoznavani(capabilities?.receiptAnalysisProvider)
    const popisky: Record<AnalysisMode, string> = {
        SERVER: `Aplikací (${nastroj})`,
        MCP: 'AI asistentem (MCP)',
        BOTH: 'Obojím a porovnat výsledky',
    }

    const pozadej = async (rezim: AnalysisMode) => {
        const prirazeno = receipt.items.some((item) => item.memberIds.length > 0)
        if (prirazeno && !window.confirm('Nový výsledek nahradí položky účtenky i s přiřazením osob. Pokračovat?')) {
            return
        }

        setOdesila(true)
        try {
            const vysledek = await actionApiCall(() => analyzeReceipt({ path: { receiptId: receipt.id }, body: { analysisMode: rezim } }), {
                successMessage: HLASKA[rezim],
            })
            if (vysledek) {
                onRequested(vysledek)
            }
        } finally {
            setOdesila(false)
        }
    }

    const nedostupne = disabled || odesila || receipt.analysisStatus === 'RUNNING'
    const popisek =
        label ?? (receipt.analysisCount > 0 ? 'Rozpoznáno špatně?' : receipt.analysisStatus === 'FAILED' ? 'Zkusit znovu' : 'Rozpoznat')
    const obsah = (
        <>
            {odesila ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="bi bi-arrow-repeat me-1" aria-hidden="true" />}
            {popisek}
        </>
    )

    // Jediná možnost se nabídne rovnou tlačítkem, bez rozbalovací nabídky.
    if (rezimy.length === 1) {
        return (
            <Button variant={variant} size={size} className="su-tappable" disabled={nedostupne} onClick={() => void pozadej(rezimy[0]!)}>
                {obsah}
            </Button>
        )
    }

    return (
        <Dropdown>
            <Dropdown.Toggle variant={variant} size={size} className="su-tappable" disabled={nedostupne} id={`precist-znovu-${receipt.id}`}>
                {obsah}
            </Dropdown.Toggle>
            {/* Pevné umístění, aby nabídku neořízl posuvný obsah dialogu. */}
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Header>Kdo účtenku přečte</Dropdown.Header>
                {rezimy.map((rezim) => (
                    <Dropdown.Item key={rezim} as="button" type="button" className="su-tappable" onClick={() => void pozadej(rezim)}>
                        {popisky[rezim]}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    )
}
